import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, fmtDate } from "@/lib/helpers";
import {
  BarChart, Bar, XAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";
import {
  Building2, Layers, IndianRupee, Radio, Users, FileBarChart, ShieldCheck,
  ReceiptIndianRupee, Settings as SettingsIcon, ScrollText, UserCog, Wallet,
  Loader2, Activity, ArrowRight,
} from "lucide-react";

const greet = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
};

const STAT = ({ label, value, icon: Icon, from, to, live, testid }) => (
  <div className="relative overflow-hidden rounded-2xl bg-white border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={testid}>
    <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full bg-gradient-to-br ${from} ${to} opacity-20`} />
    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${from} ${to} text-white flex items-center justify-center shadow`}>
      <Icon size={20} />
    </div>
    <div className="mt-4 text-3xl font-heading font-extrabold text-slate-900 tabular">{value}</div>
    <div className="mt-0.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
      {label}
      {live && <span className="inline-flex items-center gap-1 text-emerald-500"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />Live</span>}
    </div>
  </div>
);

const TOOLS = [
  { label: "Schools", icon: Building2, to: "/admin-overview", from: "from-indigo-500", toC: "to-blue-500" },
  { label: "Admin Fees", icon: ReceiptIndianRupee, to: "/admin-fees", from: "from-emerald-500", toC: "to-teal-500" },
  { label: "Subscriptions", icon: Layers, to: "/admin-fees", from: "from-violet-500", toC: "to-purple-500" },
  { label: "Permissions", icon: UserCog, to: "/users", from: "from-amber-500", toC: "to-orange-500" },
  { label: "Reports", icon: FileBarChart, to: "/reports", from: "from-sky-500", toC: "to-cyan-500" },
  { label: "Fee Setting", icon: Wallet, to: "/fee-settings", from: "from-rose-500", toC: "to-pink-500" },
  { label: "Settings", icon: SettingsIcon, to: "/settings", from: "from-slate-500", toC: "to-slate-700" },
  { label: "Audit Log", icon: ScrollText, to: "/audit", from: "from-fuchsia-500", toC: "to-pink-500" },
];

export default function SuperAdminDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/platform/dashboard").then((r) => setD(r.data)).catch(() => setD({}));
  }, []);

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  const now = new Date();
  const first = (user?.name || "Super").split(" ")[0];

  return (
    <div className="space-y-5" data-testid="superadmin-dashboard">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 text-indigo-600 text-[11px] font-bold uppercase tracking-wider px-3 py-1" data-testid="platform-console-badge">
            <Activity size={13} /> Platform Console
          </span>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-2">{greet()}, {first} <span className="inline-block">👋</span></h1>
          <p className="text-slate-500 text-sm mt-0.5">Platform overview · {now.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <STAT testid="stat-schools" label="Schools on Platform" value={d.schools ?? 0} icon={Building2} from="from-indigo-500" to="to-blue-600" />
        <STAT testid="stat-subscriptions" label="Active Subscriptions" value={d.active_subscriptions ?? 0} icon={Layers} from="from-emerald-500" to="to-teal-600" />
        <STAT testid="stat-revenue" label="Revenue This Month" value={fmt(d.revenue_month ?? 0)} icon={IndianRupee} from="from-amber-500" to="to-orange-600" />
        <STAT testid="stat-online" label="Online Right Now" value={d.online ?? 0} icon={Radio} from="from-violet-500" to="to-purple-600" live />
        <STAT testid="stat-users" label="Total Users" value={d.total_users ?? 0} icon={Users} from="from-rose-500" to="to-pink-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading font-bold text-slate-900">Platform Hub</h3>
              <span className="text-xs text-slate-400">{TOOLS.length} tools — everything you run the platform with</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {TOOLS.map((t) => (
                <button key={t.label} onClick={() => nav(t.to)} data-testid={`hub-${t.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="group flex flex-col items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-5 hover:border-indigo-300 hover:-translate-y-0.5 hover:shadow-md transition-all">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${t.from} ${t.toC} text-white flex items-center justify-center shadow`}>
                    <t.icon size={20} />
                  </div>
                  <span className="text-sm font-medium text-slate-700 group-hover:text-indigo-600">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h3 className="font-heading font-bold text-slate-900 mb-4">Revenue — last 6 months</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={d.chart || []}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="amount" fill="#7C3AED" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="live-activity-panel">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-heading font-bold text-slate-900">Live Activity</h3>
            <button onClick={() => nav("/audit")} className="text-xs text-indigo-600 font-medium hover:underline flex items-center gap-0.5" data-testid="live-activity-all">All <ArrowRight size={12} /></button>
          </div>
          <p className="text-xs text-slate-400 mb-3">Recent logins, payments &amp; changes</p>
          {(d.recent || []).length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No recent activity.</p>
          ) : (
            <div className="space-y-1 max-h-[460px] overflow-y-auto pr-1">
              {(d.recent || []).map((a, i) => (
                <div key={i} className="flex items-start gap-3 py-2.5 border-b border-slate-50 last:border-0" data-testid={`activity-${i}`}>
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0 mt-0.5"><Activity size={14} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 truncate">{a.action || "Activity"}</div>
                    <div className="text-xs text-slate-400 truncate">{a.user || "—"}{a.module ? ` · ${a.module}` : ""}</div>
                  </div>
                  <div className="text-[11px] text-slate-400 shrink-0 whitespace-nowrap">{a.time ? fmtDate(a.time) : ""}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
